import Highcharts from 'highcharts';
import exportingModule from 'highcharts/modules/exporting';
import offlineExportingModule from 'highcharts/modules/offline-exporting';
import { cloneDeep } from 'lodash-es';
import {
  computeAutoFontFactor,
  ELEMENT_SIZE_MULTIPLIER,
  mimeTypeFor,
  resolveOutputSize,
  scaleFontSizesInPlace,
  scaleNumericInPlace,
} from './utils';
import {
  LABEL_MULTIPLIER_KEY,
  type ChartImageExporter,
  type ElementSize,
  type ExportImageOptions,
  type ExportLegendItem,
  type HighchartsElementDescriptor,
  type HighchartsElementHiderMap,
  type HighchartsExporterConfig,
  type HighchartsExporterFeatures,
  type HighchartsExporterPresets,
  type OutputFormat,
} from './types';

export type { HighchartsElementDescriptor, HighchartsExporterConfig };

// ─── Built-in defaults ────────────────────────────────────────────────────────
// All consumer-overridable. Values reproduce the original Wells-page tuning so
// no caller's image changes unless it opts in to a new config.

// Per-preset axis tick spacing. Higher value = fewer ticks.
const DEFAULT_TICK_PIXEL_INTERVAL_BY_SIZE: Record<ElementSize, number> = {
  small: 180,
  medium: 180,
  large: 180,
  xlarge: 500, // unused at XL — see DEFAULT_TARGET_TICK_COUNT_BY_SIZE below.
};

// XL uses an explicit tickInterval derived from the visible extremes so the
// label count is bounded regardless of zoom range / plot pixel size. Other
// presets fall back to tickPixelInterval (plot-pixel relative).
const DEFAULT_TARGET_TICK_COUNT_BY_SIZE: Partial<Record<ElementSize, number>> =
  {
    xlarge: 3,
  };

// Min/max clamps for per-key numeric scaling so a high userFactor doesn't
// produce comically thick lines / oversized markers.
const DEFAULT_VISUAL_CLAMPS: Record<string, readonly [number, number]> = {
  radius: [2, 32],
  lineWidth: [0.5, 12],
  symbolStrokeWidth: [0.5, 8],
};

const DEFAULT_TITLE_FONT_SIZE_PX = '18px';
const DEFAULT_X_AXIS_TITLE_MARGIN = 5;
const DEFAULT_Y_AXIS_TITLE_MARGIN = 10;

// Register Highcharts exporting + offline-exporting modules (idempotent).
exportingModule(Highcharts);
offlineExportingModule(Highcharts);

function toArr<T>(x: T | T[] | undefined): T[] {
  if (!x) return [];
  return Array.isArray(x) ? x : [x];
}

interface ScaledExport {
  options: Highcharts.Options;
  // Exporting fields that MUST be passed as the first arg of
  // chart.exportChartLocal — see comment in renderAndDownload.
  exportingOverrides: {
    scale: number;
    sourceWidth: number;
    sourceHeight: number;
  };
}

// Resolved config — every optional field has been folded into a concrete value
// so the per-export pipeline doesn't need to re-resolve defaults at each step.
interface ResolvedConfig {
  tickPixelIntervalBySize: Record<ElementSize, number>;
  targetTickCountBySize: Partial<Record<ElementSize, number>>;
  visualClamps: Record<string, readonly [number, number]>;
  titleFontSizePx: string;
  axisTitleMarginBase: { x: number; y: number };
  features: Required<HighchartsExporterFeatures>;
  filterSeries: (series: Highcharts.SeriesOptionsType) => boolean;
  elementHiders: HighchartsElementHiderMap;
  xAxisTitle?: string;
  legendItems?: ExportLegendItem[];
  footerLines?: string[];
  copyPlotLines?: boolean;
}

function resolveConfig(
  presets: HighchartsExporterPresets | undefined,
  features: HighchartsExporterFeatures | undefined,
  filterSeries: HighchartsExporterConfig['filterSeries'],
  extraElementHiders: HighchartsElementHiderMap | undefined,
  xAxisTitle: string | undefined,
  legendItems: ExportLegendItem[] | undefined,
  footerLines: string[] | undefined,
  copyPlotLines: boolean | undefined
): ResolvedConfig {
  return {
    xAxisTitle,
    legendItems,
    footerLines,
    copyPlotLines,
    tickPixelIntervalBySize:
      presets?.tickPixelIntervalBySize ?? DEFAULT_TICK_PIXEL_INTERVAL_BY_SIZE,
    targetTickCountBySize:
      presets?.targetTickCountBySize ?? DEFAULT_TARGET_TICK_COUNT_BY_SIZE,
    visualClamps: presets?.visualClamps ?? DEFAULT_VISUAL_CLAMPS,
    titleFontSizePx: presets?.titleFontSizePx ?? DEFAULT_TITLE_FONT_SIZE_PX,
    axisTitleMarginBase: {
      x: presets?.axisTitleMarginBase?.x ?? DEFAULT_X_AXIS_TITLE_MARGIN,
      y: presets?.axisTitleMarginBase?.y ?? DEFAULT_Y_AXIS_TITLE_MARGIN,
    },
    features: {
      applySparseTicks: features?.applySparseTicks ?? true,
      disableHtmlDataLabels: features?.disableHtmlDataLabels ?? true,
      injectLabelMultiplier: features?.injectLabelMultiplier ?? true,
    },
    // Built-in hiders win on collision so the standard vocabulary
    // (title/legend/axisTitles/axisLabels/dataLabels) keeps consistent semantics.
    elementHiders: {
      ...(extraElementHiders ?? {}),
      ...BUILT_IN_ELEMENT_HIDERS,
    },
    filterSeries: filterSeries ?? (() => true),
  };
}

function buildScaledOptions(
  chart: Highcharts.Chart,
  opts: ExportImageOptions,
  output: { width: number; height: number },
  cfg: ResolvedConfig
): ScaledExport {
  // Must NOT use Highcharts.merge — it shares array references, so our
  // in-place mutations below would leak back into the live chart.
  const cloned: Highcharts.Options = cloneDeep(chart.userOptions);

  // Rebuild series from live userOptions (captures runtime series.update) and
  // replace `data` with current point.options (setData's soft-update path
  // doesn't write back to userOptions.data).
  cloned.series = chart.series
    .filter((s) => !(s.userOptions as { isInternal?: boolean }).isInternal)
    .map((s) => {
      const seriesOpts = cloneDeep(
        s.userOptions
      ) as Highcharts.SeriesOptionsType & { data?: unknown };
      seriesOpts.data = s.points.map((p) =>
        cloneDeep((p as Highcharts.Point & { options: unknown }).options)
      );
      return seriesOpts;
    });

  // Highcharts' exporting module accumulates `internalKey` on axis.userOptions
  // across exports; stripping our clone prevents sandbox-copy desync.
  stripHighchartsInternals(cloned);

  const userFactor = ELEMENT_SIZE_MULTIPLIER[opts.elementSize];
  // Highcharts' raster `scale` multiplies pixels uniformly but does NOT
  // touch fontSize strings or numeric option values (lineWidth/radius) in
  // the SVG. Two-part compensation:
  //   autoFactor — sandbox/live ratio, so 11px labels don't render at
  //                ~0.6% of a 1920-wide canvas (i.e. unreadable specks);
  //   userFactor — preset multiplier, so XL has visibly more prominent
  //                labels/markers than S at the same fit-to-window zoom.
  const fontFactor =
    computeAutoFontFactor(output.width, chart.chartWidth) * userFactor;
  // Apply title BEFORE the font scaler so its `style.fontSize` participates
  // in scaling along with every other text element in the options tree.
  applyExportTitle(cloned, opts, cfg.titleFontSizePx);
  applyExportXAxisTitle(cloned, cfg.xAxisTitle);
  // Before the font scaler so the caption's fontSize scales with the rest of the text.
  applyExportFooter(cloned, cfg.footerLines);
  seedDefaultFontSizes(cloned);
  scaleFontSizesInPlace(cloned, fontFactor);
  // Markers & line widths only scale by userFactor — autoFactor would push
  // weight-of-ink past readable on small live charts. Clamped to visualClamps.
  scaleNumericInPlace(cloned, userFactor, cfg.visualClamps);

  copyExtremesInto(cloned, chart);
  if (cfg.copyPlotLines) copyPlotLinesInto(cloned, chart);
  stripAxisEvents(cloned);
  if (cfg.features.applySparseTicks) {
    applySparseTickOverrides(
      cloned,
      opts.elementSize,
      cfg.tickPixelIntervalBySize,
      cfg.targetTickCountBySize
    );
  }
  scaleAxisTitleMargins(cloned, fontFactor, cfg.axisTitleMarginBase);
  if (cfg.features.injectLabelMultiplier) {
    injectLabelMultiplier(cloned, userFactor);
  }
  if (cfg.features.disableHtmlDataLabels) {
    // useHTML text renders as DOM divs getChartHTML drops (and isn't measured, so
    // title/axes clip) — render it all as SVG text instead. See disableHtmlText.
    disableHtmlText(cloned);
  }
  // Drop hand-tuned pixel offsets so Highcharts re-derives them for the scaled font.
  // Must run after applyExportTitle/disableHtmlText (they re-spread the originals).
  stripLiveTunedOffsets(cloned);
  // Consumer-supplied series filter (e.g. wells page strips gun-barrel label
  // scatter series). Default is keep-everything.
  cloned.series = (cloned.series ?? []).filter(cfg.filterSeries);
  // Export-only legend for charts whose on-screen legend is a React component.
  // Before applyElementVisibility so the drawer's Legend toggle can still hide it.
  applyExportLegend(cloned, cfg.legendItems);
  applyElementVisibility(cloned, opts.elements, cfg.elementHiders);

  cloned.chart = {
    ...(cloned.chart ?? {}),
    animation: false,
    // events.load: undefined keeps the offscreen instance from re-zooming.
    events: { ...(cloned.chart?.events ?? {}), load: undefined },
    // White background unless the chart explicitly set one.
    backgroundColor: cloned.chart?.backgroundColor ?? '#ffffff',
  };
  // Never watermark exports with the Highcharts.com credit — charts that don't
  // disable it themselves would otherwise show it in the downloaded image.
  cloned.credits = { ...(cloned.credits ?? {}), enabled: false };
  // Drop fixed horizontal margins (on-screen grid alignment) so the export auto-
  // sizes; at export scale (larger fonts) they'd clip the axis title/labels.
  delete cloned.chart.marginLeft;
  delete cloned.chart.marginRight;
  // Disable the initial series grow-animation (plotOptions.series.animation, not
  // chart.animation) so getChartHTML doesn't capture a mid-tween/empty plot.
  cloned.plotOptions = cloned.plotOptions ?? {};
  cloned.plotOptions.series = {
    ...(cloned.plotOptions.series ?? {}),
    animation: false,
  };

  return {
    options: cloned,
    exportingOverrides: {
      scale: userFactor,
      sourceWidth: output.width,
      sourceHeight: output.height,
    },
  };
}

// Strip Highcharts-injected bookkeeping fields so chartCopy.update doesn't
// re-confuse the sandbox copy with accumulated internalKey values.
const HIGHCHARTS_INTERNAL_KEYS = new Set(['internalKey', 'isInternal']);
function stripHighchartsInternals(node: unknown): void {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) {
    node.forEach(stripHighchartsInternals);
    return;
  }
  const obj = node as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    if (HIGHCHARTS_INTERNAL_KEYS.has(key)) {
      delete obj[key];
      continue;
    }
    const value = obj[key];
    if (value && typeof value === 'object') stripHighchartsInternals(value);
  }
}

// Scale axis title margin to keep the rotated yAxis title from colliding
// with the now-larger axis labels. Highcharts defaults to 10px (vertical)
// / 5px (horizontal) — both are too tight once labels grow ~5× at XL.
function scaleAxisTitleMargins(
  cloned: Highcharts.Options,
  fontFactor: number,
  baseMargins: { x: number; y: number }
): void {
  const scale = (axis: Highcharts.AxisOptions, base: number) => {
    if (!axis.title) return;
    const current =
      typeof axis.title.margin === 'number' ? axis.title.margin : base;
    axis.title.margin = Math.round(current * fontFactor);
  };
  toArr(cloned.xAxis).forEach((axis) => scale(axis, baseMargins.x));
  toArr(cloned.yAxis).forEach((axis) => scale(axis, baseMargins.y));
}

// Strip axis-level event handlers (afterSetExtremes etc.) from the cloned
// options. The offscreen chart sets extremes during init via
// `copyExtremesInto` — without this, those handlers would fire into the
// live React setters (setIsZoomed, onZoomChange) from a non-live chart.
function stripAxisEvents(cloned: Highcharts.Options): void {
  for (const axis of [...toArr(cloned.xAxis), ...toArr(cloned.yAxis)]) {
    if (axis.events) axis.events = undefined;
  }
}

// Mirror live zoom state into the cloned options (Highcharts' built-in
// extremes reflection uses internalKey, which we stripped above).
function copyExtremesInto(
  cloned: Highcharts.Options,
  live: Highcharts.Chart
): void {
  const copyAxis = (
    target: Highcharts.AxisOptions | undefined,
    source: Highcharts.Axis | undefined
  ) => {
    if (!target || !source) return;
    const ext = source.getExtremes();
    if (typeof ext.min === 'number') target.min = ext.min;
    if (typeof ext.max === 'number') target.max = ext.max;
  };
  toArr(cloned.xAxis).forEach((opts, i) => copyAxis(opts, live.xAxis[i]));
  toArr(cloned.yAxis).forEach((opts, i) => copyAxis(opts, live.yAxis[i]));
}

// Highcharts' built-in defaults for the slots where labels read as "fontSize".
// Used to seed `style.fontSize` on charts that didn't set one explicitly, so
// `scaleFontSizesInPlace` has a value to multiply.
const HIGHCHARTS_DEFAULT_AXIS_LABEL_FONT_SIZE = '11px';
const HIGHCHARTS_DEFAULT_AXIS_TITLE_FONT_SIZE = '11px';
const HIGHCHARTS_DEFAULT_LEGEND_ITEM_FONT_SIZE = '12px';

function seedDefaultFontSizes(cloned: Highcharts.Options): void {
  const seed = (
    container: { style?: { fontSize?: string } | undefined } | undefined,
    fontSizePx: string
  ): void => {
    if (!container) return;
    if (!container.style) {
      (container as { style: { fontSize: string } }).style = {
        fontSize: fontSizePx,
      };
      return;
    }
    if (!container.style.fontSize) {
      container.style.fontSize = fontSizePx;
    }
  };

  for (const axis of [...toArr(cloned.xAxis), ...toArr(cloned.yAxis)]) {
    if (!axis.labels) axis.labels = {};
    seed(axis.labels, HIGHCHARTS_DEFAULT_AXIS_LABEL_FONT_SIZE);
    if (axis.title) seed(axis.title, HIGHCHARTS_DEFAULT_AXIS_TITLE_FONT_SIZE);
  }

  // legend.itemStyle.fontSize sits directly on itemStyle (no nested `style`).
  // scaleFontSizesInPlace still finds it because it walks every `fontSize` key.
  if (cloned.legend?.enabled !== false) {
    if (!cloned.legend) cloned.legend = {};
    if (!cloned.legend.itemStyle) {
      cloned.legend.itemStyle = {
        fontSize: HIGHCHARTS_DEFAULT_LEGEND_ITEM_FONT_SIZE,
      };
    } else if (!cloned.legend.itemStyle.fontSize) {
      cloned.legend.itemStyle.fontSize =
        HIGHCHARTS_DEFAULT_LEGEND_ITEM_FONT_SIZE;
    }
  }
}

// Fill the title from the drawer-supplied chartLabel when toggled on and
// the chart didn't already provide one (wells charts render their title
// as a sibling React element, so chart.title is empty by default).
function applyExportTitle(
  cloned: Highcharts.Options,
  opts: ExportImageOptions,
  defaultFontSizePx: string
): void {
  if (opts.elements.title === false) return;
  const label = opts.chartLabel?.trim();
  const existing = cloned.title?.text;
  // A useHTML title is a DOM element getChartHTML drops — fall back to the plain
  // drawer label rendered as SVG text (useHTML: false) so the title always shows.
  const isHtmlTitle = cloned.title?.useHTML === true;
  const hasExistingText =
    !isHtmlTitle && typeof existing === 'string' && existing.trim().length > 0;
  if (!label && !hasExistingText) return;

  cloned.title = {
    ...(cloned.title ?? {}),
    text: hasExistingText ? existing : label,
    useHTML: false,
    style: {
      // Default first so a chart-supplied fontSize wins on the spread below.
      fontSize: defaultFontSizePx,
      ...(cloned.title?.style ?? {}),
    },
  };
}

// Render an export-only x-axis title for charts whose on-screen x-axis label lives
// in an external switcher. Applied to the first x-axis only when it has none.
function applyExportXAxisTitle(
  cloned: Highcharts.Options,
  xAxisTitle: string | undefined
): void {
  if (!xAxisTitle) return;
  const axes = toArr(cloned.xAxis);
  const target = axes[0];
  if (target) {
    if (!target.title?.text) {
      target.title = { ...(target.title ?? {}), text: xAxisTitle };
    }
  } else {
    cloned.xAxis = { title: { text: xAxisTitle } };
  }
}

// Base fontSize for the export-only caption; seeded so scaleFontSizesInPlace scales it.
const DEFAULT_FOOTER_FONT_SIZE_PX = '12px';

/**
 * Renders export-only footer lines as the chart caption (bottom of the exported image), for
 * summary numbers that live OUTSIDE the chart on screen (e.g. the Cash Flow cumulative table
 * below it) so the exported image is self-contained. `useHTML: false` serializes the text into
 * the SVG (getChartHTML drops DOM); a seeded fontSize lets `scaleFontSizesInPlace` size it with
 * the rest of the text. Mutates `cloned` in place (a per-export deep clone). Exported for tests.
 *
 * @param {Highcharts.Options} cloned - The deep-cloned export options, mutated in place.
 * @param {string[] | undefined} footerLines - One string per caption line. No-op when empty.
 * @return {void}
 */
export function applyExportFooter(
  cloned: Highcharts.Options,
  footerLines: string[] | undefined
): void {
  if (!footerLines?.length) return;
  cloned.caption = {
    ...(cloned.caption ?? {}),
    text: footerLines.join('<br/>'),
    useHTML: false,
    align: 'left',
    // Fresh export-only caption (charts don't set one on screen), so no prior style to merge.
    // Highcharts types the caption's `fontSize` as a number, but the SVG renderer (and our font
    // scaler) use a px string like every other text element — cast to satisfy TS.
    style: { fontSize: DEFAULT_FOOTER_FONT_SIZE_PX as unknown as number },
  };
}

/**
 * Copies the live chart's plotLines/plotBands into the export clone. `buildScaledOptions` rebuilds
 * from `userOptions`, which does NOT carry plotlines added imperatively via `axis.addPlotLine`
 * (e.g. the Cash Flow "Eff Date" line) — so without this they vanish from the export. The live
 * `plotLinesAndBands` collection holds BOTH declarative + imperative entries, so we overwrite the
 * clone's per-axis plotLines/plotBands from it (the live truth). Gated by the `copyPlotLines`
 * config so other exporter consumers are unaffected.
 *
 * @param {Highcharts.Options} cloned - The deep-cloned export options, mutated in place.
 * @param {Highcharts.Chart} live - The live chart to read current plotlines from.
 * @return {void}
 */
export function copyPlotLinesInto(
  cloned: Highcharts.Options,
  live: Highcharts.Chart
): void {
  type BandOrLine = Highcharts.AxisPlotLinesOptions &
    Highcharts.AxisPlotBandsOptions;
  const copyAxis = (
    target: Highcharts.AxisOptions | undefined,
    source: Highcharts.Axis | undefined
  ): void => {
    if (!target || !source) return;
    const entries = (
      source as unknown as { plotLinesAndBands?: { options?: BandOrLine }[] }
    ).plotLinesAndBands;
    if (!entries?.length) return;
    const optionsList = entries
      .map((entry) => entry.options)
      .filter((o): o is BandOrLine => Boolean(o));
    const plotLines = optionsList.filter((o) => o.value !== undefined);
    const plotBands = optionsList.filter(
      (o) => o.from !== undefined || o.to !== undefined
    );
    if (plotLines.length) target.plotLines = cloneDeep(plotLines);
    if (plotBands.length) target.plotBands = cloneDeep(plotBands);
  };
  toArr(cloned.xAxis).forEach((opts, i) => copyAxis(opts, live.xAxis[i]));
  toArr(cloned.yAxis).forEach((opts, i) => copyAxis(opts, live.yAxis[i]));
}

/**
 * Appends a data-less legend-only series per item so a chart whose on-screen legend is a React
 * component (its Highcharts legend disabled) gets a matching legend in the exported image. Enabling
 * the legend for these items would otherwise ALSO surface the real data series, so the real series
 * are hidden first — avoiding a doubled legend. `checked` prefixes a ☑/☐ glyph. No-op without items.
 * Mutates `cloned` in place; safe because it's a per-export deep clone, never the live chart.
 * Exported for unit testing.
 *
 * @param {Highcharts.Options} cloned - The deep-cloned export options, mutated in place.
 * @param {ExportLegendItem[] | undefined} legendItems - The export-only legend entries.
 * @return {void}
 */
export function applyExportLegend(
  cloned: Highcharts.Options,
  legendItems: ExportLegendItem[] | undefined
): void {
  if (!legendItems?.length) return;
  // symbolRadius:0 makes the block swatches sharp squares (Highcharts' default
  // rounds them into circles) to match the on-screen colour-square legend.
  cloned.legend = { ...(cloned.legend ?? {}), enabled: true, symbolRadius: 0 };
  // Hide the real data series from the now-enabled legend so only the export-only items below show
  // (otherwise the export doubles up: real series names + these items). See the JSDoc for why safe.
  for (const series of cloned.series ?? []) {
    series.showInLegend = false;
  }
  const legendSeries = legendItems.map(
    (item): Highcharts.SeriesOptionsType => ({
      // `column` gives a filled colour-block swatch; threshold:null keeps this
      // data-less series off the axis baseline (safe on log axes).
      type: 'column',
      name:
        typeof item.checked === 'boolean'
          ? `${item.checked ? '☑' : '☐'} ${item.label}`
          : item.label,
      color: item.color,
      data: [],
      threshold: null,
      showInLegend: true,
      enableMouseTracking: false,
    })
  );
  cloned.series = [...(cloned.series ?? []), ...legendSeries];
}

// Pass userFactor to per-series formatters via series.custom so they can
// implement size-aware label thinning. Generic — formatter decides usage.
function injectLabelMultiplier(
  cloned: Highcharts.Options,
  userFactor: number
): void {
  for (const s of cloned.series ?? []) {
    const series = s as { custom?: Record<string, unknown> };
    series.custom = {
      ...(series.custom ?? {}),
      [LABEL_MULTIPLIER_KEY]: userFactor,
    };
  }
}

// Flip useHTML→false on every text element (data/axis labels, titles, stack labels,
// subtitle, legend) so it serializes into the SVG and is measured (no drop, no clip).
function disableHtmlText(cloned: Highcharts.Options): void {
  const disable = (cfg: unknown): unknown => {
    if (!cfg || typeof cfg !== 'object') return cfg;
    if (Array.isArray(cfg)) return cfg.map(disable);
    return { ...(cfg as Record<string, unknown>), useHTML: false };
  };
  // Data labels (plotOptions + per-series).
  const plot = cloned.plotOptions ?? {};
  for (const key of Object.keys(plot)) {
    const type = plot[key as keyof typeof plot] as
      { dataLabels?: unknown } | undefined;
    if (type?.dataLabels) type.dataLabels = disable(type.dataLabels);
  }
  for (const s of cloned.series ?? []) {
    const series = s as { dataLabels?: unknown };
    if (series.dataLabels) series.dataLabels = disable(series.dataLabels);
  }
  // Axis labels / titles / stack labels (x and y).
  for (const axis of [...toArr(cloned.xAxis), ...toArr(cloned.yAxis)]) {
    const a = axis as {
      labels?: unknown;
      title?: unknown;
      stackLabels?: unknown;
    };
    if (a.labels) a.labels = disable(a.labels);
    if (a.title) a.title = disable(a.title);
    if (a.stackLabels) a.stackLabels = disable(a.stackLabels);
  }
  // Subtitle (the chart title is handled by applyExportTitle).
  if (cloned.subtitle) {
    cloned.subtitle = disable(cloned.subtitle) as Highcharts.SubtitleOptions;
  }
  // useHTML legends render as DOM overlays getChartHTML drops (swatches, no text).
  // Render as SVG text and strip tags from any HTML labelFormatter output.
  const legend = cloned.legend as
    | { useHTML?: boolean; labelFormatter?: (this: unknown) => unknown }
    | undefined;
  if (legend?.useHTML) {
    const original = legend.labelFormatter;
    cloned.legend = {
      ...legend,
      useHTML: false,
      ...(typeof original === 'function'
        ? {
            labelFormatter: function (this: unknown): unknown {
              const out = original.call(this);
              return typeof out === 'string' ? stripHtmlTags(out) : out;
            },
          }
        : {}),
    } as Highcharts.LegendOptions;
  }
}

// Reduce an HTML fragment to plain text for SVG rendering (can't parse block HTML).
// Drops tags and decodes the few entities our formatters emit.
function stripHtmlTags(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

// Live charts hand-tune title.y / axis-label x/y for the small on-screen font; at
// export scale those fixed offsets clip/overlap, so drop them for Highcharts to re-derive.
function stripLiveTunedOffsets(cloned: Highcharts.Options): void {
  if (cloned.title) delete (cloned.title as { y?: number }).y;
  if (cloned.subtitle) delete (cloned.subtitle as { y?: number }).y;
  for (const axis of [...toArr(cloned.xAxis), ...toArr(cloned.yAxis)]) {
    const labels = axis.labels as { x?: number; y?: number } | undefined;
    if (labels) {
      delete labels.x;
      delete labels.y;
    }
  }
}

// Sparse ticks for slide-deck sizes. Clears the live chart's denser
// tickPositioner/tickInterval. At XL we lock the count via an explicit
// nice-rounded tickInterval (see targetTickCountBySize); other sizes
// fall back to Highcharts' tickPixelInterval-based spacing.
// NOTE: do NOT set tickAmount — it overrides startOnTick/endOnTick and
// expands the axis past live extremes.
function applySparseTickOverrides(
  cloned: Highcharts.Options,
  elementSize: ElementSize,
  tickPixelIntervalBySize: Record<ElementSize, number>,
  targetTickCountBySize: Partial<Record<ElementSize, number>>
): void {
  const tickPixelInterval = tickPixelIntervalBySize[elementSize];
  const targetCount = targetTickCountBySize[elementSize];
  const override = (axis: Highcharts.AxisOptions) => {
    axis.tickPositioner = undefined;
    axis.minorTickInterval = 0;
    axis.minorGridLineWidth = 0;
    axis.startOnTick = false;
    axis.endOnTick = false;
    // Disable Highcharts' default `text-overflow: ellipsis` — scaled-up
    // labels can outgrow their tick slot and prefer to be dropped entirely
    // (via the default `allowOverlap: false`) over truncated to "-2,5…".
    axis.labels = {
      ...(axis.labels ?? {}),
      style: { ...(axis.labels?.style ?? {}), textOverflow: 'none' },
    };

    if (
      targetCount &&
      typeof axis.min === 'number' &&
      typeof axis.max === 'number' &&
      axis.max !== axis.min
    ) {
      // Compute a round tickInterval that yields ~targetCount labels across
      // [min, max]. Wins over the live's stale tickInterval because it's a
      // real number (not undefined), so Highcharts.merge propagates it.
      const range = Math.abs(axis.max - axis.min);
      axis.tickInterval = niceTickInterval(range / targetCount);
      axis.tickPixelInterval = undefined;
    } else {
      axis.tickInterval = undefined;
      axis.tickPixelInterval = tickPixelInterval;
    }
  };
  [...toArr(cloned.xAxis), ...toArr(cloned.yAxis)].forEach(override);
}

// Round `raw` up to the nearest "nice" number (1, 2, 5, 10 × 10^n) so axis
// labels read cleanly (e.g. 500 instead of 433.33).
function niceTickInterval(raw: number): number {
  if (!isFinite(raw) || raw <= 0) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const normalized = raw / magnitude;
  let nice: number;
  if (normalized <= 1) nice = 1;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 5) nice = 5;
  else nice = 10;
  return nice * magnitude;
}

// Drawer-key → Highcharts option mutation. Unknown keys are ignored so
// page-specific descriptors that don't map to a Highcharts option are no-ops.
// Consumers can extend this vocabulary via `extraElementHiders` on the config.
const BUILT_IN_ELEMENT_HIDERS: HighchartsElementHiderMap = {
  title: (cloned) => {
    cloned.title = { ...(cloned.title ?? {}), text: undefined };
  },
  legend: (cloned) => {
    cloned.legend = { ...(cloned.legend ?? {}), enabled: false };
  },
  axisTitles: (cloned) => {
    for (const axis of [...toArr(cloned.xAxis), ...toArr(cloned.yAxis)]) {
      axis.title = { ...(axis.title ?? {}), text: undefined };
    }
  },
  axisLabels: (cloned) => {
    for (const axis of [...toArr(cloned.xAxis), ...toArr(cloned.yAxis)]) {
      axis.labels = { ...(axis.labels ?? {}), enabled: false };
    }
  },
  dataLabels: (cloned) => {
    cloned.plotOptions = cloned.plotOptions ?? {};
    cloned.plotOptions.series = {
      ...(cloned.plotOptions.series ?? {}),
      dataLabels: {
        ...(cloned.plotOptions.series?.dataLabels ?? {}),
        enabled: false,
      },
    };
    // Series-level dataLabels override plotOptions, so walk them too.
    for (const s of cloned.series ?? []) {
      const series = s as { dataLabels?: unknown };
      if (Array.isArray(series.dataLabels)) {
        series.dataLabels = series.dataLabels.map((dl) => ({
          ...(dl as Record<string, unknown>),
          enabled: false,
        }));
      } else if (series.dataLabels) {
        series.dataLabels = {
          ...(series.dataLabels as Record<string, unknown>),
          enabled: false,
        };
      } else {
        series.dataLabels = { enabled: false };
      }
    }
  },
};

function applyElementVisibility(
  cloned: Highcharts.Options,
  elements: Record<string, boolean>,
  hiderMap: HighchartsElementHiderMap
): void {
  for (const [key, visible] of Object.entries(elements)) {
    if (visible !== false) continue;
    const hider = hiderMap[key];
    if (hider) hider(cloned);
  }
}

// Offscreen sandbox using Highcharts' own DOM helpers — avoids
// `exportChartLocal`'s `merge(chart.options, ...)` which leaks array refs
// (markers/lineWidth) back into the live chart at high presets.
function createOffscreenHost(width: number, height: number): HTMLDivElement {
  return Highcharts.createElement(
    'div',
    undefined,
    {
      position: 'absolute',
      left: '-100000px',
      top: '0',
      width: `${width}px`,
      height: `${height}px`,
    } as unknown as Highcharts.CSSObject,
    document.body
  ) as HTMLDivElement;
}

function renderAndDownload(
  scaled: ScaledExport,
  filename: string,
  format: OutputFormat
): Promise<boolean> {
  // Defensive clone — `Highcharts.chart()` consumes the options it's handed.
  const safeOptions = cloneDeep(scaled.options);
  const { sourceWidth, sourceHeight, scale } = scaled.exportingOverrides;

  return new Promise<boolean>((resolve, reject) => {
    const host = createOffscreenHost(sourceWidth, sourceHeight);
    let offscreen: Highcharts.Chart | null = null;

    const cleanup = () => {
      if (offscreen) {
        offscreen.destroy();
        offscreen = null;
      }
      Highcharts.discardElement(host);
    };

    try {
      // Pin width/height so the offscreen instance renders at export size.
      safeOptions.chart = {
        ...(safeOptions.chart ?? {}),
        width: sourceWidth,
        height: sourceHeight,
      };

      offscreen = Highcharts.chart(host, safeOptions);
      const svg = (
        offscreen as Highcharts.Chart & {
          sanitizeSVG: (svg: string, options: Highcharts.Options) => string;
        }
      ).sanitizeSVG(offscreen.getChartHTML(), safeOptions);

      Highcharts.downloadSVGLocal(
        svg,
        {
          type: mimeTypeFor(format) as 'image/png' | 'image/jpeg',
          filename: stripExtension(filename),
          scale,
          // Disable Highcharts' default POST to export.highcharts.com — keep export fully local.
          fallbackToExportServer: false,
        },
        (err: unknown) => {
          cleanup();
          const message =
            err instanceof Error ? err.message : err ? String(err) : 'unknown';
          reject(
            new Error(
              `Highcharts export failed (${message}). Please try again.`
            )
          );
        },
        () => {
          cleanup();
          resolve(true);
        }
      );
    } catch (err) {
      cleanup();
      const message = err instanceof Error ? err.message : String(err);
      reject(
        new Error(
          `Highcharts export failed (${message}). Try closing this dialog ` +
            'and reopening it; if the error persists, refresh the page.'
        )
      );
    }
  });
}

// downloadSVGLocal appends its own extension — strip to avoid `foo.png.png`.
function stripExtension(filename: string): string {
  return filename.replace(/\.(png|jpe?g|svg)$/i, '');
}

/**
 * Renders the scaled chart offscreen and rasterizes it to a PNG/JPEG {@link Blob} — the
 * blob-returning sibling of {@link renderAndDownload} (which only downloads). It renders the
 * chart offscreen from its `options` (so a mounted-but-hidden chart still exports — no visible
 * surface needed, unlike SciChart), serializes it to an SVG data URL, draws that into a canvas
 * at the export resolution, and reads the canvas back as a Blob.
 *
 * Resolves `null` (never rejects) when the raster can't be produced — no 2D context, an SVG
 * that fails to load, or a tainted canvas (an external image would poison the read; the
 * Reserves chart is pure vector, so this shouldn't arise) — so one image's failure only drops
 * that image from the export rather than aborting it.
 *
 * @param {ScaledExport} scaled - The scaled options + export-size overrides (from `buildScaledOptions`).
 * @param {OutputFormat} format - `png` or `jpeg`.
 * @return {Promise<Blob | null>} The rasterized image, or null when it couldn't be produced.
 */
function renderToBlob(
  scaled: ScaledExport,
  format: OutputFormat
): Promise<Blob | null> {
  // Defensive clone — `Highcharts.chart()` consumes the options it's handed.
  const safeOptions = cloneDeep(scaled.options);
  const { sourceWidth, sourceHeight, scale } = scaled.exportingOverrides;

  return new Promise<Blob | null>((resolve) => {
    const host = createOffscreenHost(sourceWidth, sourceHeight);
    let offscreen: Highcharts.Chart | null = null;
    const cleanup = () => {
      if (offscreen) {
        offscreen.destroy();
        offscreen = null;
      }
      Highcharts.discardElement(host);
    };

    try {
      // Pin width/height so the offscreen instance renders at export size.
      safeOptions.chart = {
        ...(safeOptions.chart ?? {}),
        width: sourceWidth,
        height: sourceHeight,
      };
      offscreen = Highcharts.chart(host, safeOptions);
      const svg = (
        offscreen as Highcharts.Chart & {
          sanitizeSVG: (svg: string, options: Highcharts.Options) => string;
        }
      ).sanitizeSVG(offscreen.getChartHTML(), safeOptions);

      // Rasterize: SVG data URL → <img> → canvas (at export resolution) → Blob.
      const image = new Image();
      const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
        svg
      )}`;
      image.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(sourceWidth * scale);
          canvas.height = Math.round(sourceHeight * scale);
          const context = canvas.getContext('2d');
          if (!context) {
            cleanup();
            resolve(null);
            return;
          }
          // JPEG has no alpha — lay a white ground so transparent areas aren't black.
          if (format === 'jpeg') {
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);
          }
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            cleanup();
            resolve(blob);
          }, mimeTypeFor(format));
        } catch {
          // Tainted canvas / raster failure — drop this image, never sink the export.
          cleanup();
          resolve(null);
        }
      };
      image.onerror = () => {
        cleanup();
        resolve(null);
      };
      image.src = svgUrl;
    } catch {
      cleanup();
      resolve(null);
    }
  });
}

export function createHighchartsExporter(
  config: HighchartsExporterConfig
): ChartImageExporter {
  const {
    getChart,
    elementHooks: { getElements },
    filterSeries,
    extraElementHiders,
    presets,
    features,
    xAxisTitle,
    legendItems,
    footerLines,
    copyPlotLines,
  } = config;

  const cfg = resolveConfig(
    presets,
    features,
    filterSeries,
    extraElementHiders,
    xAxisTitle,
    legendItems,
    footerLines,
    copyPlotLines
  );

  return {
    getLiveSize: () => {
      const chart = getChart();
      if (!chart) return null;
      return { width: chart.chartWidth, height: chart.chartHeight };
    },
    getElements: () =>
      getElements().map(({ key, label, available }) => ({
        key,
        label,
        available,
      })),
    exportAndDownload: (opts, filename) => {
      const chart = getChart();
      if (!chart) return Promise.resolve(false);
      const liveSize = { width: chart.chartWidth, height: chart.chartHeight };
      const output = resolveOutputSize(opts, liveSize);
      if (!output) return Promise.resolve(false);
      const scaled = buildScaledOptions(chart, opts, output, cfg);
      return renderAndDownload(scaled, filename, opts.outputFormat);
    },
    exportToBlob: (opts) => {
      const chart = getChart();
      if (!chart) return Promise.resolve(null);
      const liveSize = { width: chart.chartWidth, height: chart.chartHeight };
      const output = resolveOutputSize(opts, liveSize);
      if (!output) return Promise.resolve(null);
      const scaled = buildScaledOptions(chart, opts, output, cfg);
      return renderToBlob(scaled, opts.outputFormat);
    },
  };
}
